import React, { useState, useRef, useEffect, createContext, useContext } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'

interface DropdownContextType {
  closeDropdown: () => void
}

const DropdownContext = createContext<DropdownContextType | null>(null)

interface DropdownProps {
  trigger: React.ReactNode
  children: React.ReactNode
  className?: string
}

export function Dropdown({ trigger, children, className = '' }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number; openUpward: boolean; maxHeight: number } | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownContentRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  const closeDropdown = () => {
    setIsOpen(false)
    setDropdownPosition(null)
  }

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Calculate dropdown position synchronously
  const calculatePosition = () => {
    if (!triggerRef.current) return null

    const rect = triggerRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - rect.bottom
    
    // User requested to keep it below ("garde sous le menu")
    // So we disable the flip logic and always open downward.
    const openUpward = false

    return {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
      openUpward: false,
      maxHeight: spaceBelow - 20 // Stays within page bounds
    }
  }

  const handleToggle = () => {
    if (!isOpen) {
      // Calculate position before opening
      const position = calculatePosition()
      if (position) {
        setDropdownPosition(position)
        setIsOpen(true)
      }
    } else {
      setIsOpen(false)
      setDropdownPosition(null)
    }
  }

  // Recalculate position on scroll/resize when open
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const updatePosition = () => {
        const position = calculatePosition()
        if (position) {
          setDropdownPosition(position)
        }
      }

      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)

      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [isOpen])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      const isClickInsideTrigger = triggerRef.current?.contains(target)
      const isClickInsideDropdown = dropdownContentRef.current?.contains(target)

      if (!isClickInsideTrigger && !isClickInsideDropdown) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  const getTransform = () => {
    if (!isOpen || !dropdownPosition) return 'translateY(-8px) scale(0.95)'
    if (dropdownPosition.openUpward) return 'translateY(-100%) scale(1)'
    return 'translateY(0) scale(1)'
  }

  const dropdownContent = dropdownPosition ? (
    <div
      ref={dropdownContentRef}
      className={`w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl shadow-2xl dark:shadow-2xl z-[9999] transition-all duration-200 ease-out fixed ${isOpen
          ? 'opacity-100'
          : 'opacity-0 pointer-events-none'
        }`}
      style={{
        top: `${dropdownPosition.top}px`,
        right: `${dropdownPosition.right}px`,
        transform: getTransform()
      }}
    >
      <div className="py-2 overflow-hidden flex flex-col">
        <div 
          className={`transition-all duration-200 overflow-y-auto custom-scrollbar ${isOpen ? 'translate-y-0' : '-translate-y-2'}`}
          style={{ maxHeight: dropdownPosition ? `${dropdownPosition.maxHeight}px` : 'none' }}
        >
          {children}
        </div>
      </div>
    </div>
  ) : null

  return (
    <DropdownContext.Provider value={{ closeDropdown }}>
      <div className={`relative ${className}`} ref={dropdownRef}>
        <div ref={triggerRef} onClick={handleToggle} className="cursor-pointer">
          {trigger}
        </div>

        {isOpen && dropdownPosition ? createPortal(dropdownContent, document.body) : null}
      </div>
    </DropdownContext.Provider>
  )
}

interface DropdownItemProps {
  children: React.ReactNode
  onClick?: () => void
  className?: string
}

export function DropdownItem({ children, onClick, className = '' }: DropdownItemProps) {
  const context = useContext(DropdownContext)

  const handleClick = () => {
    if (onClick) {
      onClick()
    }
    // Always close dropdown after item click
    if (context) {
      context.closeDropdown()
    }
  }

  return (
    <button
      className={`w-full text-left px-4 py-3.5 text-sm transition-all duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-inset ${className}`}
      onClick={handleClick}
    >
      {children}
    </button>
  )
}
